import React, { useState, useMemo } from 'react'
import { useDepartmentComparison, useProgramComparison } from '../../hooks/useAnalytics'
import {
  RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer
} from 'recharts'
import { Building2, GraduationCap, Loader2, AlertTriangle, ArrowLeftRight, Info } from 'lucide-react'

interface Props {
  department: string | null
  isSuperAdmin: boolean
  programs: { code: string; name: string; department: string }[]
}

const COLORS = ['#1e3a5f', '#6366f1', '#0891b2', '#059669', '#d97706', '#dc2626', '#7c3aed', '#db2777']

const ComparativeTab: React.FC<Props> = ({ department, isSuperAdmin, programs }) => {
  const [mode, setMode] = useState<'department' | 'program'>(isSuperAdmin ? 'department' : 'program')
  const [selectedPrograms, setSelectedPrograms] = useState<string[]>([])

  const { data: deptData, loading: deptLoading, error: deptError } = useDepartmentComparison()
  const { data: progData, loading: progLoading, error: progError } = useProgramComparison(
    department,
    selectedPrograms.length > 0 ? selectedPrograms : null
  )

  // Toggle program selection (max 5)
  const toggleProgram = (code: string) => {
    setSelectedPrograms(prev => {
      if (prev.includes(code)) return prev.filter(c => c !== code)
      if (prev.length >= 5) return prev
      return [...prev, code]
    })
  }

  // ── Department Comparison Data ──────────────────────────
  const deptBarData = useMemo(() => {
    if (!deptData) return []
    return deptData.map(d => ({
      name: d.department_name,
      'Employment Rate': d.employment_rate ?? 0,
      'Avg Outcome Score': d.avg_peo_score ?? 0,
      'Job Relevance': d.job_relevance_rate ?? 0,
    }))
  }, [deptData])

  const deptRadarData = useMemo(() => {
    if (!deptData) return []
    const metrics = ['employment_rate', 'avg_peo_score', 'job_relevance_rate'] as const
    const labels: Record<string, string> = {
      employment_rate: 'Employment',
      avg_peo_score: 'Outcome Score',
      job_relevance_rate: 'Job Relevance',
    }
    return metrics.map(m => {
      const point: Record<string, any> = { metric: labels[m] }
      deptData.forEach(d => {
        point[d.department_name] = d[m] ?? 0
      })
      return point
    })
  }, [deptData])

  // ── Program Comparison Data ──────────────────────────────
  const progBarData = useMemo(() => {
    if (!progData) return []
    return progData.map(p => ({
      name: p.program_code,
      'Employment Rate': p.employment_rate ?? 0,
      'Avg PEO Score': p.avg_peo_score ?? 0,
      'Avg PO Score': p.avg_po_score ?? 0,
      'Job Relevance': p.job_relevance_rate ?? 0,
      'Salary >40k': p.salary_above_40k_rate ?? 0,
    }))
  }, [progData])

  const progRadarData = useMemo(() => {
    if (!progData) return []
    const metrics = ['employment_rate', 'avg_peo_score', 'avg_po_score', 'job_relevance_rate', 'salary_above_40k_rate'] as const
    const labels: Record<string, string> = {
      employment_rate: 'Employment',
      avg_peo_score: 'PEO Score',
      avg_po_score: 'PO Score',
      job_relevance_rate: 'Job Relevance',
      salary_above_40k_rate: 'Salary >40k',
    }
    return metrics.map(m => {
      const point: Record<string, any> = { metric: labels[m] }
      progData.forEach(p => {
        point[p.program_code] = p[m] ?? 0
      })
      return point
    })
  }, [progData])

  const activeDeptNames = useMemo(() => deptData?.map(d => d.department_name) ?? [], [deptData])
  const activeProgCodes = useMemo(() => progData?.map(p => p.program_code) ?? [], [progData])

  const isLoading = mode === 'department' ? deptLoading : progLoading
  const activeError = mode === 'department' ? deptError : progError

  return (
    <div className="space-y-6">
      {/* Mode Selector */}
      <div className="flex items-center gap-3">
        {isSuperAdmin && (
          <button
            onClick={() => setMode('department')}
            className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-lg border transition ${
              mode === 'department'
                ? 'bg-blue-900 text-white border-blue-900'
                : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
            }`}
          >
            <Building2 className="w-4 h-4" />
            Department Comparison
          </button>
        )}
        <button
          onClick={() => setMode('program')}
          className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-lg border transition ${
            mode === 'program'
              ? 'bg-blue-900 text-white border-blue-900'
              : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
          }`}
        >
          <GraduationCap className="w-4 h-4" />
          Program Comparison
        </button>
      </div>

      {/* Program Multi-Select (program mode) */}
      {mode === 'program' && (
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs font-bold text-gray-500 uppercase tracking-wide">Select programs to compare (max 5)</p>
            {selectedPrograms.length > 0 && (
              <button onClick={() => setSelectedPrograms([])} className="text-xs text-gray-500 hover:text-gray-700">Clear</button>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            {programs.map(p => {
              const isSelected = selectedPrograms.includes(p.code)
              const isDisabled = !isSelected && selectedPrograms.length >= 5
              return (
                <button
                  key={p.code}
                  onClick={() => !isDisabled && toggleProgram(p.code)}
                  disabled={isDisabled}
                  className={`px-3 py-1.5 text-xs font-medium rounded-lg border transition ${
                    isSelected
                      ? 'bg-blue-900 text-white border-blue-900'
                      : isDisabled
                        ? 'bg-gray-50 text-gray-300 border-gray-100 cursor-not-allowed'
                        : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  {p.code}
                </button>
              )
            })}
          </div>
        </div>
      )}

      {/* Loading */}
      {isLoading && (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-6 h-6 animate-spin text-blue-900" />
          <span className="ml-2 text-sm text-gray-500">Loading comparison data...</span>
        </div>
      )}

      {/* Error */}
      {activeError && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-red-500 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-red-800">Failed to load comparison data</p>
            <p className="text-xs text-red-600 mt-0.5">{activeError}</p>
          </div>
        </div>
      )}

      {/* ══════════ Department Comparison View ══════════ */}
      {mode === 'department' && !deptLoading && !deptError && deptData && (
        <>
          {/* Info banner */}
          <div className="flex items-start gap-2.5 bg-blue-50 border border-blue-200 rounded-xl px-4 py-3">
            <Info className="w-4 h-4 text-blue-500 mt-0.5 shrink-0" />
            <p className="text-xs text-blue-700">
              <span className="font-bold">This view always shows all departments</span> — it is designed to compare them side by side and is not affected by the filter bar above. To scope data to a specific department or program, switch to <span className="font-bold">Program Comparison</span>.
            </p>
          </div>

          {/* Comparison Table */}
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <h3 className="text-sm font-bold text-gray-700 mb-4">Department Overview</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="text-left px-3 py-2 text-xs font-bold text-gray-500 uppercase">Department</th>
                    <th className="text-center px-3 py-2 text-xs font-bold text-gray-500 uppercase">Alumni</th>
                    <th className="text-center px-3 py-2 text-xs font-bold text-gray-500 uppercase">Employment</th>
                    <th className="text-center px-3 py-2 text-xs font-bold text-gray-500 uppercase">Avg Outcome</th>
                    <th className="text-center px-3 py-2 text-xs font-bold text-gray-500 uppercase">Relevance</th>
                    <th className="text-left px-3 py-2 text-xs font-bold text-gray-500 uppercase">Top Program</th>
                  </tr>
                </thead>
                <tbody>
                  {deptData.map((d, i) => (
                    <tr key={d.department_name} className={i % 2 === 0 ? '' : 'bg-gray-50/50'}>
                      <td className="px-3 py-2.5 font-medium text-gray-900">{d.department_name}</td>
                      <td className="text-center px-3 py-2.5 text-gray-700">{d.total_alumni}</td>
                      <td className="text-center px-3 py-2.5">
                        <span className={`font-bold ${(d.employment_rate ?? 0) >= 70 ? 'text-green-600' : (d.employment_rate ?? 0) >= 50 ? 'text-yellow-600' : 'text-red-600'}`}>
                          {d.employment_rate ?? 0}%
                        </span>
                      </td>
                      <td className="text-center px-3 py-2.5 text-gray-700">{d.avg_peo_score ?? '—'}</td>
                      <td className="text-center px-3 py-2.5 text-gray-700">{d.job_relevance_rate ?? '—'}%</td>
                      <td className="px-3 py-2.5 text-gray-600 text-xs">{d.top_program || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Charts */}
          <div className="flex flex-col gap-6">
            {/* Bar Chart — full width */}
            {deptBarData.length > 0 && (
              <div className="bg-white rounded-xl border border-gray-200 p-5">
                <h3 className="text-sm font-bold text-gray-700 mb-4">Key Metrics by Department</h3>
                <div className="overflow-x-auto">
                  <div style={{ minWidth: Math.max(800, deptBarData.length * 120) }}>
                    <ResponsiveContainer width="100%" height={450}>
                      <BarChart data={deptBarData} margin={{ top: 5, right: 20, left: 0, bottom: 80 }}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" tick={{ fontSize: 10 }} angle={-35} textAnchor="end" interval={0} height={100} />
                        <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} />
                        <Tooltip />
                        <Legend wrapperStyle={{ fontSize: 12 }} />
                        <Bar dataKey="Employment Rate" fill="#22c55e" radius={[2, 2, 0, 0]} />
                        <Bar dataKey="Avg Outcome Score" fill="#6366f1" radius={[2, 2, 0, 0]} />
                        <Bar dataKey="Job Relevance" fill="#0891b2" radius={[2, 2, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>
            )}

            {/* Radar and other charts in a 2-col grid below */}
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">

            {/* Radar Chart */}
            {deptRadarData.length > 0 && activeDeptNames.length <= 8 && (
              <div className="bg-white rounded-xl border border-gray-200 p-5">
                <h3 className="text-sm font-bold text-gray-700 mb-4">Department Radar Comparison</h3>
                <ResponsiveContainer width="100%" height={350}>
                  <RadarChart data={deptRadarData}>
                    <PolarGrid />
                    <PolarAngleAxis dataKey="metric" tick={{ fontSize: 11 }} />
                    <PolarRadiusAxis angle={30} domain={[0, 100]} tick={{ fontSize: 10 }} />
                    {activeDeptNames.map((name, i) => (
                      <Radar
                        key={name}
                        name={name}
                        dataKey={name}
                        stroke={COLORS[i % COLORS.length]}
                        fill={COLORS[i % COLORS.length]}
                        fillOpacity={0.15}
                      />
                    ))}
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Tooltip />
                  </RadarChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
          </div>
        </>
      )}

      {/* ══════════ Program Comparison View ══════════ */}
      {mode === 'program' && !progLoading && !progError && progData && progData.length > 0 && (
        <>
          {/* Comparison Table */}
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <h3 className="text-sm font-bold text-gray-700 mb-4">Program Overview</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="text-left px-3 py-2 text-xs font-bold text-gray-500 uppercase">Program</th>
                    <th className="text-center px-3 py-2 text-xs font-bold text-gray-500 uppercase">Alumni</th>
                    <th className="text-center px-3 py-2 text-xs font-bold text-gray-500 uppercase">Employment</th>
                    <th className="text-center px-3 py-2 text-xs font-bold text-gray-500 uppercase">PEO Avg</th>
                    <th className="text-center px-3 py-2 text-xs font-bold text-gray-500 uppercase">PO Avg</th>
                    <th className="text-center px-3 py-2 text-xs font-bold text-gray-500 uppercase">Relevance</th>
                    <th className="text-center px-3 py-2 text-xs font-bold text-gray-500 uppercase">Salary &gt;40k</th>
                  </tr>
                </thead>
                <tbody>
                  {progData.map((p, i) => (
                    <tr key={p.program_code} className={i % 2 === 0 ? '' : 'bg-gray-50/50'}>
                      <td className="px-3 py-2.5 font-medium text-gray-900">{p.program_code}</td>
                      <td className="text-center px-3 py-2.5 text-gray-700">{p.total_alumni}</td>
                      <td className="text-center px-3 py-2.5">
                        <span className={`font-bold ${(p.employment_rate ?? 0) >= 70 ? 'text-green-600' : (p.employment_rate ?? 0) >= 50 ? 'text-yellow-600' : 'text-red-600'}`}>
                          {p.employment_rate ?? 0}%
                        </span>
                      </td>
                      <td className="text-center px-3 py-2.5 text-gray-700">{p.avg_peo_score ?? '—'}</td>
                      <td className="text-center px-3 py-2.5 text-gray-700">{p.avg_po_score ?? '—'}</td>
                      <td className="text-center px-3 py-2.5 text-gray-700">{p.job_relevance_rate ?? '—'}%</td>
                      <td className="text-center px-3 py-2.5 text-gray-700">{p.salary_above_40k_rate ?? '—'}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Charts */}
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
            {/* Bar Chart — full width when radar is hidden (>5 programs) */}
            {progBarData.length > 0 && (
              <div className={`bg-white rounded-xl border border-gray-200 p-5 ${activeProgCodes.length > 5 ? 'xl:col-span-2' : ''}`}>
                <h3 className="text-sm font-bold text-gray-700 mb-4">Key Metrics by Program</h3>
                <ResponsiveContainer width="100%" height={activeProgCodes.length > 5 ? 400 : 350}>
                  <BarChart data={progBarData} margin={activeProgCodes.length > 5 ? { left: 8, right: 8, bottom: 8 } : undefined}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                    <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Bar dataKey="Employment Rate" fill="#22c55e" radius={[2, 2, 0, 0]} />
                    <Bar dataKey="Avg PEO Score" fill="#6366f1" radius={[2, 2, 0, 0]} />
                    <Bar dataKey="Job Relevance" fill="#0891b2" radius={[2, 2, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}

            {/* Radar */}
            {progRadarData.length > 0 && activeProgCodes.length <= 5 && (
              <div className="bg-white rounded-xl border border-gray-200 p-5">
                <h3 className="text-sm font-bold text-gray-700 mb-4">Program Radar Comparison</h3>
                <ResponsiveContainer width="100%" height={350}>
                  <RadarChart data={progRadarData}>
                    <PolarGrid />
                    <PolarAngleAxis dataKey="metric" tick={{ fontSize: 11 }} />
                    <PolarRadiusAxis angle={30} domain={[0, 100]} tick={{ fontSize: 10 }} />
                    {activeProgCodes.map((code, i) => (
                      <Radar
                        key={code}
                        name={code}
                        dataKey={code}
                        stroke={COLORS[i % COLORS.length]}
                        fill={COLORS[i % COLORS.length]}
                        fillOpacity={0.15}
                      />
                    ))}
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Tooltip />
                  </RadarChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        </>
      )}

      {/* Empty state for program mode — no programs selected */}
      {mode === 'program' && !progLoading && !progError && (!progData || progData.length === 0) && selectedPrograms.length === 0 && (
        <div className="text-center py-16 bg-white rounded-xl border border-gray-200">
          <ArrowLeftRight className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <h3 className="text-base font-bold text-gray-700 mb-1">Select Programs to Compare</h3>
          <p className="text-sm text-gray-500">Choose up to 5 programs above to see side-by-side metrics.</p>
        </div>
      )}

      {/* Empty state for program mode — programs selected but no alumni data */}
      {mode === 'program' && !progLoading && !progError && progData && progData.length === 0 && selectedPrograms.length > 0 && (
        <div className="text-center py-16 bg-white rounded-xl border border-gray-200">
          <ArrowLeftRight className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <h3 className="text-base font-bold text-gray-700 mb-1">No Alumni in Selected Programs</h3>
          <p className="text-sm text-gray-500">
            None of the selected programs
            {department ? <> in <span className="font-medium">{department}</span></> : ''} have approved alumni records yet.
          </p>
          <p className="text-xs text-gray-400 mt-1.5">Try selecting different programs or check that alumni have been approved.</p>
        </div>
      )}
    </div>
  )
}

export default ComparativeTab
